import { Container } from 'pixi.js';
import { getTexture, makeSprite } from '@/assets/resolver';
import { GAME } from '@/config/game';
import {
  BUNKER, DASHER, DOME, DRONE, ELITE, FLY, MITE, SNIPER, SPLITTER, STATS,
  TURRET, WEAVER, type MinionKind,
} from './minion-defs';
import type { BulletSystem } from './bullets';
import {
  aimedShot, chase, clampToArena, deathSpray, shoot, shotAt, type Minion,
} from './minion-motion';

export type { MinionKind } from './minion-defs';


/**
 * Enemy catalogue (docs/12 M2) — light kinds die in 1-2 hits, variety comes
 * from formation and behavior. Tuning lives in minion-defs.ts.
 */
export class MinionSystem {
  readonly view = new Container();
  private minions: Minion[] = [];
  /** Active piece rect (World / advance director) — minions never leave it */
  private readonly bounds = { x: 0, y: 0, w: GAME.arena.width as number, h: GAME.arena.height as number };

  setBounds(width: number, height: number, x = 0, y = 0): void {
    Object.assign(this.bounds, { x, y, w: width, h: height });
  }

  spawn(kind: MinionKind, count: number, x: number, y: number, summoned = false): void {
    for (let i = 0; i < count; i++) {
      const s = STATS[kind];
      const sprite = makeSprite(s.texKey);
      this.view.addChild(sprite);
      const angle = (Math.PI * 2 * i) / count + 0.7;
      const spread = count > 1 ? 60 : 0;
      this.minions.push({
        kind,
        x: x + Math.cos(angle) * spread,
        y: y + Math.sin(angle) * spread,
        vx: 0, vy: 0,
        hp: s.hp,
        orbitAngle: angle,
        fireTimer: 1 + i * 0.5,
        animT: i * 0.3,
        aux: i % 2 === 0 ? 1 : -1,
        summoned,
        sprite,
      });
    }
  }

  update(
    dt: number, bossX: number, bossY: number, px: number, py: number, bullets: BulletSystem,
  ): void {
    for (const m of this.minions) {
      m.animT += dt;
      const dx = px - m.x;
      const dy = py - m.y;
      const dist = Math.hypot(dx, dy) || 1;
      switch (m.kind) {
        case 'drone': {
          m.orbitAngle += DRONE.orbitSpeed * dt;
          const tx = bossX + Math.cos(m.orbitAngle) * DRONE.orbitDist;
          const ty = bossY + Math.sin(m.orbitAngle) * DRONE.orbitDist;
          m.x += (tx - m.x) * Math.min(1, 4 * dt);
          m.y += (ty - m.y) * Math.min(1, 4 * dt);
          if (shoot(m, DRONE.fireInterval, dt)) {
            aimedShot(bullets, m, px, py, DRONE.bulletSpeed);
          }
          break;
        }
        case 'fly':
          chase(m, dx, dy, dist, FLY.accel, FLY.maxSpeed, dt);
          break;
        case 'elite': {
          m.x += (dx / dist) * ELITE.speed * dt;
          m.y += (dy / dist) * ELITE.speed * dt;
          if (shoot(m, ELITE.fireInterval, dt)) {
            for (let i = 0; i < ELITE.ringCount; i++) {
              const a = (Math.PI * 2 * i) / ELITE.ringCount + m.animT;
              shotAt(bullets, m, a, ELITE.bulletSpeed);
            }
          }
          break;
        }
        case 'bunker':
          m.orbitAngle = Math.atan2(dy, dx);
          if (shoot(m, BUNKER.fireInterval, dt)) {
            for (let i = 0; i < BUNKER.burst; i++) {
              const a = m.orbitAngle + (i - (BUNKER.burst - 1) / 2) * BUNKER.spread;
              shotAt(bullets, m, a, BUNKER.bulletSpeed);
            }
          }
          break;
        case 'turret':
          m.orbitAngle = Math.atan2(dy, dx);
          // Short leash, vicious rate: step out of its yard or melt (M9)
          if (dist < TURRET.range && shoot(m, TURRET.fireInterval, dt)) {
            shotAt(bullets, m, m.orbitAngle, TURRET.bulletSpeed, TURRET.bulletLife);
          }
          break;
        case 'dome':
          m.orbitAngle = Math.atan2(dy, dx);
          if (shoot(m, DOME.chargeTime, dt)) {
            // ONE huge shell (M3) — slow, fat, hits like a boss ring
            bullets.spawn({
              x: m.x, y: m.y,
              dirX: Math.cos(m.orbitAngle), dirY: Math.sin(m.orbitAngle),
              speed: DOME.bulletSpeed, life: DOME.bulletLife,
              radius: DOME.bulletRadius, damage: DOME.damage,
              friendly: false, texKey: 'bullet/enemy/ring', rotates: false,
            });
          }
          break;
        case 'dasher': {
          // Zigzag closer: flips sides on a timer, snapshots a shot each flip
          if (shoot(m, DASHER.zigInterval, dt)) {
            m.aux = -m.aux;
            aimedShot(bullets, m, px, py, DASHER.bulletSpeed);
          }
          const a = Math.atan2(dy, dx) + m.aux * DASHER.zigAngle;
          m.vx = Math.cos(a) * DASHER.speed;
          m.vy = Math.sin(a) * DASHER.speed;
          m.x += m.vx * dt;
          m.y += m.vy * dt;
          break;
        }
        case 'weaver': {
          // Sine-stitched drift toward the player, dropping slow mines
          const base = Math.atan2(dy, dx);
          const lateral = Math.sin(m.animT * WEAVER.waveFreq * Math.PI) * m.aux * WEAVER.waveAmp;
          m.vx = Math.cos(base) * WEAVER.speed - Math.sin(base) * lateral;
          m.vy = Math.sin(base) * WEAVER.speed + Math.cos(base) * lateral;
          m.x += m.vx * dt;
          m.y += m.vy * dt;
          if (shoot(m, WEAVER.mineInterval, dt)) {
            shotAt(bullets, m, base + Math.PI, 24, 3.4, 'bullet/enemy/ring');
          }
          break;
        }
        case 'splitter':
          m.x += (dx / dist) * SPLITTER.speed * dt;
          m.y += (dy / dist) * SPLITTER.speed * dt;
          m.vx = dx / dist;
          m.vy = dy / dist;
          break;
        case 'mite':
          chase(m, dx, dy, dist, MITE.accel, MITE.maxSpeed, dt);
          break;
        case 'sniper': {
          // Holds its comfort distance, strafes, lines up a FAST shot
          if (dist < SNIPER.comfort) {
            m.x -= (dx / dist) * SNIPER.retreat * dt;
            m.y -= (dy / dist) * SNIPER.retreat * dt;
          }
          m.x += (-dy / dist) * SNIPER.strafe * m.aux * dt;
          m.y += (dx / dist) * SNIPER.strafe * m.aux * dt;
          m.orbitAngle = Math.atan2(dy, dx);
          if (shoot(m, SNIPER.fireInterval, dt)) {
            shotAt(bullets, m, m.orbitAngle, SNIPER.bulletSpeed);
          }
          break;
        }
      }
      clampToArena(m, this.bounds, STATS[m.kind].radius);
    }
  }

  /** Player-bullet collision. @returns positions of killed minions (for fx) */
  collide(
    bullets: BulletSystem, px: number, py: number, onHitEnergy: (dmg: number) => void,
  ): Array<{ x: number; y: number }> {
    const killed: Array<{ x: number; y: number }> = [];
    for (let i = this.minions.length - 1; i >= 0; i--) {
      const m = this.minions[i]!;
      bullets.collideCircle(true, m.x, m.y, STATS[m.kind].radius, (dmg) => {
        m.hp -= dmg;
        onHitEnergy(dmg);
      });
      if (m.hp <= 0) {
        if (m.kind === 'fly') deathSpray(bullets, m, px, py);
        killed.push({ x: m.x, y: m.y });
        const wasSplitter = m.kind === 'splitter';
        const sx = m.x;
        const sy = m.y;
        m.sprite.destroy();
        this.minions.splice(i, 1);
        // Splitter bursts into mites — leaving it for last gets pricey (M2)
        if (wasSplitter) this.spawn('mite', SPLITTER.children, sx, sy);
      }
    }
    return killed;
  }

  /**
   * Area damage (mortar/rocket/missile AoE — docs/12 #5 fix): every minion
   * inside the blast takes `damage`. Mirrors collide()'s death handling so
   * bombs actually clear clustered minions instead of fizzling.
   */
  damageCircle(
    bullets: BulletSystem, x: number, y: number, radius: number, damage: number,
    onHit: (dmg: number) => void, px: number, py: number,
  ): Array<{ x: number; y: number }> {
    const killed: Array<{ x: number; y: number }> = [];
    for (let i = this.minions.length - 1; i >= 0; i--) {
      const m = this.minions[i]!;
      if (Math.hypot(m.x - x, m.y - y) > radius + STATS[m.kind].radius) continue;
      m.hp -= damage;
      onHit(damage);
      if (m.hp <= 0) {
        if (m.kind === 'fly') deathSpray(bullets, m, px, py);
        killed.push({ x: m.x, y: m.y });
        const wasSplitter = m.kind === 'splitter';
        const sx = m.x;
        const sy = m.y;
        m.sprite.destroy();
        this.minions.splice(i, 1);
        if (wasSplitter) this.spawn('mite', SPLITTER.children, sx, sy);
      }
    }
    return killed;
  }

  /**
   * Player contact check. Kamikaze kinds die on contact (positions returned
   * for explosion fx); others persist. @returns touch result
   */
  touchPlayer(px: number, py: number, playerRadius: number): { hit: boolean; died: Array<{ x: number; y: number }> } {
    let hit = false;
    const died: Array<{ x: number; y: number }> = [];
    for (let i = this.minions.length - 1; i >= 0; i--) {
      const m = this.minions[i]!;
      const s = STATS[m.kind];
      if (Math.hypot(m.x - px, m.y - py) < s.radius + playerRadius) {
        hit = true;
        if (s.kamikaze) {
          died.push({ x: m.x, y: m.y });
          m.sprite.destroy();
          this.minions.splice(i, 1);
        }
      }
    }
    return { hit, died };
  }

  render(): void {
    for (const m of this.minions) {
      m.sprite.position.set(m.x, m.y);
      const s = STATS[m.kind];
      if (s.frames > 1 && (m.kind === 'fly' || m.kind === 'weaver')) {
        m.sprite.texture = getTexture(s.texKey, Math.floor(m.animT * 14) % s.frames);
      }
      m.sprite.rotation =
        m.kind === 'drone' ? m.orbitAngle + Math.PI / 2 : Math.atan2(m.vy, m.vx);
      if (m.kind === 'elite') m.sprite.rotation = m.animT * 0.8;
      if (m.kind === 'bunker' || m.kind === 'turret' || m.kind === 'dome' || m.kind === 'sniper') {
        m.sprite.rotation = m.orbitAngle;
        // Charge/aim blink as the next shot approaches
        const blinkAt = m.kind === 'dome' ? DOME.blinkTime : 0.7;
        m.sprite.texture = getTexture(s.texKey, m.fireTimer < blinkAt ? 1 : 0);
      }
    }
  }

  clear(): void {
    for (const m of this.minions) m.sprite.destroy();
    this.minions = [];
  }

  get count(): number {
    return this.minions.length;
  }

  /** Boss-summoned minions only — arena turrets/side squads never count. */
  get summonedCount(): number {
    let n = 0;
    for (const m of this.minions) if (m.summoned) n++;
    return n;
  }

  /** Closest minion to a point — auto-aim retarget while the boss is immune. */
  nearest(x: number, y: number): { x: number; y: number } | null {
    let best: { x: number; y: number } | null = null;
    let bestD = Infinity;
    for (const m of this.minions) {
      const d = (m.x - x) ** 2 + (m.y - y) ** 2;
      if (d < bestD) {
        bestD = d;
        best = m;
      }
    }
    return best;
  }
}
